import { createServerFn } from "@tanstack/react-start";
import yn from "yn";
import { resolveAuthState, type AuthState } from "#/modules/util.auth/auth-state.server";
import { getEnvironmentInfo, type EnvironmentInfo } from "#/modules/util.environment/env.server";
import { resolveIntegreSqlDatabase } from "#/modules/util.fetch-client/fetch-client.server";
import { isUserImpersonating } from "#/modules/util.impersonate/utils.impersonate.server";

/**
 * Fully-resolved app bootstrap state for the router context, computed once in the root
 * `beforeLoad`. Every guard below the root reads these fields synchronously instead of issuing
 * its own RPC.
 */
export type RouteContext = AuthState & {
  /** Session access token carries the `impersonation` claim — see `useIsImpersonating`. */
  isImpersonating: boolean;
  /** HIDE_IMPERSONATION_BANNER is set: the sidebar shows no impersonation banner. */
  hideImpersonationBanner: boolean;
  /** Server-derived environment info (ASPNETCORE_ENVIRONMENT) — see `useEnvironment`. */
  environment: EnvironmentInfo;
  /** Isolated test database (IntegreSQL) driving this request, when leased; drives the test-DB badge. */
  integreSqlDatabase?: string;
};

/**
 * Single combined RPC that seeds the whole router context: the root `beforeLoad` is the hottest
 * path in the app (it runs on every real navigation), so each module contributes a server-side
 * slice here instead of exposing its own client-callable fn. Re-runs on `router.invalidate()`
 * and on every real navigation (see `clearRouteContextCache`), so context stays fresh.
 */
export const getRouteContext = createServerFn({ method: "GET" }).handler(
  async (): Promise<RouteContext> => {
    const auth = await resolveAuthState();
    return {
      ...auth,
      // Parsed after resolveAuthState so a token refreshed there is the one whose claim is read.
      isImpersonating: auth.user ? await isUserImpersonating() : false,
      hideImpersonationBanner: yn(process.env.HIDE_IMPERSONATION_BANNER, { default: false }),
      environment: getEnvironmentInfo(),
      // Attached even when logged out, so the test-DB badge shows under an IntegreSQL clone.
      integreSqlDatabase: resolveIntegreSqlDatabase(),
    };
  },
);
